import { useEffect, useMemo, useState } from "react";

import type { AllEInput } from "../../../functions/_lib/alle-check/types";
import { calculateAllEEligibility } from "../../../functions/_lib/alle-check/calculator";
import {
  buildRecommenderInput,
  type RecommenderFormState,
} from "./recommender-input";
import { useT } from "../../i18n/language";

const HEATING_OPTIONS = [
  { v: "Radiatoren", l: "Radiatoren" },
  { v: "Vloerverwarming", l: "Vloerverwarming" },
  { v: "Vloerverwarming + andere bronnen", l: "Vloerverwarming + andere bronnen" },
  { v: "Vloerconvectoren", l: "Vloerconvectoren" },
  { v: "Luchtverwarming", l: "Luchtverwarming" },
  { v: "Stadsverwarming", l: "Stadsverwarming" },
];

const HOUSE_TYPES = ["Vrijstaand", "2-onder-1-kap", "Tussenwoning", "Hoekwoning", "Appartement"];

const ENERGY_LABELS = ["", "A++++", "A+++", "A++", "A+", "A", "B", "C", "D", "E", "F", "G"];
const BAD_LABELS = new Set(["E", "F", "G"]);

const BUILD_YEARS = [
  { v: "voor_1970", l: "Voor 1970" },
  { v: "1970_1990", l: "1970-1990" },
  { v: "1990_2005", l: "1990-2005" },
  { v: "na_2005", l: "Na 2005" },
];

const INSULATION_OPTIONS = [
  { v: "spouwmuur", l: "Spouwmuurisolatie" },
  { v: "dak", l: "Dakisolatie" },
  { v: "vloer", l: "Vloerisolatie" },
  { v: "hr_glas", l: "HR++ of triple glas" },
];

const DISTRIBUTION_OPTIONS = [
  { v: "Radiatoren", l: "Radiatoren" },
  { v: "Vloerverwarming", l: "Vloerverwarming" },
  { v: "Vloerverwarming + andere bronnen", l: "Vloerverwarming + andere bronnen" },
];

const BOILER_AGE_OPTIONS = [
  { v: "new", l: "Nieuw < 5 jr" },
  { v: "average", l: "Gemiddeld 5-15 jr" },
  { v: "old", l: "Oud 15-25 jr" },
  { v: "very_old", l: "Zeer oud 25+ jr" },
];

const HOT_WATER_OPTIONS = [
  { v: "low", l: "Weinig" },
  { v: "average", l: "Gemiddeld" },
  { v: "high", l: "Veel" },
];

const INCOMPATIBLE_HEATING = new Set(["Luchtverwarming"]);

function defaultState(): RecommenderFormState {
  return {
    house_type: "Tussenwoning",
    apartment_vve: null,
    apartment_outdoor_space: null,
    heating_types: ["Radiatoren"],
    is_renovating: false,
    route: "gas",
    yearly_gas_m3: "1200",
    summer_avg_gas_m3: "20",
    beng1_value: "",
    floor_area_m2: "",
    build_year: "1990_2005",
    energy_label: "",
    insulation_measures: [],
    cv_comfort_55c: true,
    silent_mode_at_night: false,
    distribution_main_room: "Radiatoren",
    distribution_whole_house: "Radiatoren",
    day_temp: "20",
    night_temp: "19",
    resident_number: "",
    boiler_age: "",
    hot_water_usage: "",
    estimated_electricity_usage: "",
    insulation_score_roof: "",
    insulation_score_wall: "",
    insulation_score_floor: "",
    insulation_score_windows: "",
    postal_code: "",
    house_number: "",
    house_number_addition: "",
    address_street: "",
    address_city: "",
    cv_temp_c: "",
    manifold_pump: null,
    radiators: [
      { type: "", length_cm: "", height_cm: "", room: "" },
      { type: "", length_cm: "", height_cm: "", room: "" },
      { type: "", length_cm: "", height_cm: "", room: "" },
    ],
    radiator_coverage_pct: "70",
    zone_thermostat: "",
    extra_heat_sources: "",
    cw_class: "",
  };
}

const RADIATOR_TYPES: Array<{ v: "10" | "11" | "20" | "21" | "22" | "33"; l: string }> = [
  { v: "10", l: "Type 10" },
  { v: "11", l: "Type 11" },
  { v: "20", l: "Type 20" },
  { v: "21", l: "Type 21" },
  { v: "22", l: "Type 22" },
  { v: "33", l: "Type 33" },
];

/** W/m2 at W55. mirror of RAD_W_M2 in functions/_lib/alle-check/config.ts. */
const RAD_W_M2_UI: Record<string, number> = {
  "10": 270,
  "11": 460,
  "20": 536,
  "21": 663,
  "22": 918,
  "33": 1326,
};

interface Props {
  onSubmit: (input: AllEInput) => void;
  isSubmitting: boolean;
  onStateChange?: (state: RecommenderFormState) => void;
}

const CheckIcon = () => (
  <svg className="checkbox-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
  </svg>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    style={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 9l-7 7-7-7" />
  </svg>
);

/**
 * Inline circular info icon. Copy mirrors Bob's `ⓘ` from
 * alle-check.html (lines 254, 384, 460, 533). Click toggles a
 * <HelpPanel /> rendered by the caller.
 */
export const HelpIcon = ({
  open,
  onClick,
  label,
}: {
  open: boolean;
  onClick: () => void;
  label?: string;
}) => {
  const t = useT();
  const resolvedLabel = label ?? t("Uitleg tonen");
  return (
  <button
    type="button"
    className={`help-icon-btn ${open ? "is-open" : ""}`}
    onClick={onClick}
    aria-expanded={open}
    aria-label={resolvedLabel}
    title={resolvedLabel}
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="11" x2="12" y2="16.5" strokeLinecap="round" />
      <circle cx="12" cy="8" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  </button>
  );
};

// Help-text copy mirrors Bob's HTML (alle-check.html lines 339-610)
//. reused verbatim, lightly trimmed for inline display. Keep
// authoritative copy in sync if HTML help blocks change.
type HelpKey =
  | "s1"
  | "s2"
  | "s3"
  | "s4"
  | "s5"
  | "q_zone_thermostat"
  | "q_extra_sources"
  | "q_cw_class"
  | "q_manifold_pump";

export function CheckForm({ onSubmit, isSubmitting, onStateChange }: Props) {
  const t = useT();
  const [s, setS] = useState<RecommenderFormState>(defaultState);
  const [helpOpen, setHelpOpen] = useState<Record<HelpKey, boolean>>({
    s1: false,
    s2: false,
    s3: false,
    s4: false,
    s5: false,
    q_zone_thermostat: false,
    q_extra_sources: false,
    q_cw_class: false,
    q_manifold_pump: false,
  });
  const toggleHelp = (k: HelpKey) =>
    setHelpOpen((prev) => ({ ...prev, [k]: !prev[k] }));
  const update = <K extends keyof RecommenderFormState>(k: K, v: RecommenderFormState[K]) =>
    setS((prev) => ({ ...prev, [k]: v }));

  const toggleArrayValue = (key: "heating_types" | "insulation_measures", value: string) => {
    setS((prev) => {
      const has = prev[key].includes(value);
      return { ...prev, [key]: has ? prev[key].filter((x) => x !== value) : [...prev[key], value] };
    });
  };

  useEffect(() => {
    onStateChange?.(s);
  }, [s, onStateChange]);

  const isApartment = s.house_type === "Appartement";
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAddress, setShowAddress] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState<string | null>(null);
  const [addressLookupLoading, setAddressLookupLoading] = useState(false);

  const lookupAddress = async () => {
    setAddressLookupError(null);
    const normalizedPostcode = s.postal_code.replace(/\s+/g, "").toUpperCase();
    const normalizedHouse = s.house_number.trim();
    if (!normalizedPostcode || !normalizedHouse) {
      setAddressLookupError(t("Vul postcode en huisnummer in."));
      return;
    }
    if (!/^\d{4}[A-Z]{2}$/.test(normalizedPostcode)) {
      setAddressLookupError(t("Ongeldige postcode (verwacht: 1234AB)."));
      return;
    }
    if (normalizedPostcode !== s.postal_code) update("postal_code", normalizedPostcode);
    setAddressLookupLoading(true);
    try {
      const res = await fetch(
        `/api/v1/lookup/address?postal_code=${encodeURIComponent(normalizedPostcode)}&house_number=${encodeURIComponent(normalizedHouse)}`,
      );
      const text = await res.text();
      let body: {
        street?: string;
        city?: string;
        error?: string;
        floor_area_m2?: number | null;
        energy_label?: string | null;
      } = {};
      try {
        body = text ? (JSON.parse(text) as typeof body) : {};
      } catch {
        // Non-JSON response (likely 404 HTML from a missing route). swallow
        // the parser error and surface a friendly message instead.
        setAddressLookupError(
          res.ok
            ? t("Onverwacht antwoord van de server.")
            : `${t("Adres-lookup is op deze omgeving niet beschikbaar")} (HTTP ${res.status}).`,
        );
        return;
      }
      if (!res.ok) {
        setAddressLookupError(body.error ?? `${t("Adres-lookup faalde")} (HTTP ${res.status}).`);
        return;
      }
      update("address_street", body.street ?? "");
      update("address_city", body.city ?? "");
      // Auto-fill the model-driving inputs from BAG / EP-online so the installer
      // does not have to type them. Both stay editable.
      if (typeof body.floor_area_m2 === "number" && body.floor_area_m2 > 0) {
        update("floor_area_m2", String(body.floor_area_m2));
      }
      if (body.energy_label) {
        update("energy_label", body.energy_label);
      }
    } catch (e) {
      setAddressLookupError(e instanceof Error ? e.message : t("Onbekende fout."));
    } finally {
      setAddressLookupLoading(false);
    }
  };

  const buildInput = useMemo<AllEInput>(() => buildRecommenderInput(s), [s]);

  const heatingHasIncompatible = s.heating_types.some((h) => INCOMPATIBLE_HEATING.has(h));
  const labelIsBad = BAD_LABELS.has(s.energy_label);
  const tempDelta = Math.abs((Number(s.day_temp) || 0) - (Number(s.night_temp) || 0));
  const tempWarn = tempDelta > 4;

  // Radiator capacity step is shown when CV is hot AND the system is radiator-based.
  const cvTempNum = Number(s.cv_temp_c);
  const cvIsHot = (!s.cv_comfort_55c) || (Number.isFinite(cvTempNum) && cvTempNum > 60);
  const isRadiatorSystem = s.distribution_whole_house === "Radiatoren";
  const showRadiatorStep = cvIsHot && isRadiatorSystem;

  // Live radiator capacity totals (W55), client-side mirror of HTML Step 4.
  const radiatorTotals = useMemo(() => {
    let totalW = 0;
    const perRow: Array<{ kw: number }> = s.radiators.map((r) => {
      const wm2 = r.type ? RAD_W_M2_UI[r.type] ?? 0 : 0;
      const lenM = (Number(r.length_cm) || 0) / 100;
      const hgtM = (Number(r.height_cm) || 0) / 100;
      const w = wm2 * lenM * hgtM;
      if (w > 0) totalW += w;
      return { kw: w / 1000 };
    });
    let needed: number | null = null;
    let coveragePctNum = Number(s.radiator_coverage_pct);
    if (!Number.isFinite(coveragePctNum) || coveragePctNum <= 0) coveragePctNum = 70;
    try {
      const liveResult = calculateAllEEligibility(buildInput);
      const req = liveResult.check1_heat_demand.required_capacity_kw;
      if (Number.isFinite(req) && req > 0) needed = (req * coveragePctNum) / 100;
    } catch {
      needed = null;
    }
    const totalKw = totalW / 1000;
    const capPct = needed && needed > 0 ? Math.round((totalKw / needed) * 100) : null;
    return { perRow, totalKw, needed, capPct, coveragePctNum };
  }, [s.radiators, s.radiator_coverage_pct, buildInput]);

  const addRadiator = () => {
    setS((prev) => ({
      ...prev,
      radiators: [...prev.radiators, { type: "", length_cm: "", height_cm: "", room: "" }],
    }));
  };
  const removeRadiator = (idx: number) => {
    setS((prev) => ({
      ...prev,
      radiators: prev.radiators.filter((_, i) => i !== idx),
    }));
  };
  const updateRadiator = (
    idx: number,
    patch: Partial<RecommenderFormState["radiators"][number]>,
  ) => {
    setS((prev) => ({
      ...prev,
      radiators: prev.radiators.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };

  const insulationSliders: Array<{
    name: string;
    key:
      | "insulation_score_roof"
      | "insulation_score_wall"
      | "insulation_score_floor"
      | "insulation_score_windows";
  }> = [
    { name: t("Dak"), key: "insulation_score_roof" },
    { name: t("Wand"), key: "insulation_score_wall" },
    { name: t("Vloer"), key: "insulation_score_floor" },
    { name: t("Ramen"), key: "insulation_score_windows" },
  ];

  return (
    <form
      className="form-column"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(buildInput);
      }}
    >
      {/* Section 0. Adres (optioneel) */}
      <div className="card warm">
        <button
          type="button"
          className={`accordion-trigger ${showAddress ? "is-open" : ""}`}
          onClick={() => setShowAddress((v) => !v)}
          aria-expanded={showAddress}
        >
          <div className="section-header no-margin">
            <span className="step-badge">0</span>
            <h2 className="section-title">{t("Adres (optioneel)")}</h2>
          </div>
          <ChevronIcon open={showAddress} />
        </button>
        <div className={`accordion-content ${showAddress ? "open" : ""}`}>
          <p className="card-subtitle">{t("Vul postcode + huisnummer in om straat en plaats automatisch op te halen.")}</p>
          <div className="input-row cols-3">
            <div className="input-group">
              <label className="input-label">{t("Postcode")}</label>
              <input
                className="text-input"
                type="text"
                value={s.postal_code}
                onChange={(e) => update("postal_code", e.target.value.toUpperCase())}
                placeholder="1062HJ"
              />
            </div>
            <div className="input-group">
              <label className="input-label">{t("Huisnummer")}</label>
              <input
                className="text-input"
                type="text"
                value={s.house_number}
                onChange={(e) => update("house_number", e.target.value)}
                placeholder="29"
              />
            </div>
            <div className="input-group">
              <label className="input-label">{t("Toevoeging")}</label>
              <input
                className="text-input"
                type="text"
                value={s.house_number_addition}
                onChange={(e) => update("house_number_addition", e.target.value)}
                placeholder="A"
              />
            </div>
          </div>
          <div className="address-row">
            <button
              type="button"
              className="btn-secondary"
              onClick={lookupAddress}
              disabled={addressLookupLoading}
            >
              {addressLookupLoading ? t("Bezig...") : t("Adres opzoeken")}
            </button>
            {addressLookupError && <span style={{ color: "var(--danger)", fontSize: 13 }}>{addressLookupError}</span>}
            {s.address_street && (
              <span className="address-resolved">
                {s.address_street}, {s.address_city}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Section 1. Woningtype & uitsluiting */}
      <div className="card">
        <div className="section-header">
          <span className="step-badge">1</span>
          <h2 className="section-title">{t("Woningtype & uitsluiting")}</h2>
          <HelpIcon open={helpOpen.s1} onClick={() => toggleHelp("s1")} label={t("Uitleg woningtype")} />
        </div>
        <div className={`help-panel ${helpOpen.s1 ? "help-panel-open" : ""}`}>
          <div className="help-panel-eyebrow">{t("Uitleg")}</div>
          <p>
            {t("Bepaalt of een Quatt All-Electric warmtepomp (Solo of Duo) past bij dit woningtype. Appartementen zijn alleen geschikt als de VVE toestemming geeft en er buitenruimte is voor de buitenunit. Selecteer ook het bestaande verwarmingstype. Luchtverwarming en stadsverwarming zijn niet compatibel met All-Electric.")}
          </p>
          <p>
            <strong>{t("Verwarmingstype:")}</strong> {t("meerdere selecties mogelijk. Een woning met vloerverwarming én radiatoren telt mee als gemengd systeem.")}
          </p>
        </div>

        <div className="question">
          <label className="question-label">{t("Woningtype")}</label>
          <div className="pill-group">
            {HOUSE_TYPES.map((ht) => (
              <button
                key={ht}
                type="button"
                className={`pill-btn ${s.house_type === ht ? "active" : ""}`}
                onClick={() => update("house_type", ht)}
              >
                {t(ht)}
              </button>
            ))}
          </div>
        </div>

        {isApartment && (
          <>
            <div className="question">
              <label className="question-label">{t("VVE-toestemming verkregen?")}</label>
              <div className="pill-group">
                <button type="button" className={`pill-btn ${s.apartment_vve === true ? "active" : ""}`} onClick={() => update("apartment_vve", true)}>{t("Ja")}</button>
                <button type="button" className={`pill-btn ${s.apartment_vve === false ? "active" : ""}`} onClick={() => update("apartment_vve", false)}>{t("Nee")}</button>
              </div>
            </div>
            <div className="question">
              <label className="question-label">{t("Buitenruimte beschikbaar voor buitenunit?")}</label>
              <div className="pill-group">
                <button type="button" className={`pill-btn ${s.apartment_outdoor_space === true ? "active" : ""}`} onClick={() => update("apartment_outdoor_space", true)}>{t("Ja")}</button>
                <button type="button" className={`pill-btn ${s.apartment_outdoor_space === false ? "active" : ""}`} onClick={() => update("apartment_outdoor_space", false)}>{t("Nee")}</button>
              </div>
            </div>
          </>
        )}

        <div className="question">
          <label className="question-label">{t("Verwarmingstype (meerdere mogelijk)")}</label>
          <div className="checkbox-grid">
            {HEATING_OPTIONS.map((h) => {
              const checked = s.heating_types.includes(h.v);
              return (
                <div
                  key={h.v}
                  role="checkbox"
                  aria-checked={checked}
                  tabIndex={0}
                  className={`checkbox-card ${checked ? "checked" : ""}`}
                  onClick={() => toggleArrayValue("heating_types", h.v)}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      toggleArrayValue("heating_types", h.v);
                    }
                  }}
                >
                  <div className="custom-checkbox">
                    <CheckIcon />
                  </div>
                  <span className="checkbox-label">{t(h.l)}</span>
                </div>
              );
            })}
          </div>
          {heatingHasIncompatible && (
            <div className="warning-hint">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {t("Let op: niet compatibel met All-Electric.")}
            </div>
          )}
        </div>
      </div>

      {/* Section 2. Berekeningsmethode */}
      <div className="card">
        <div className="section-header">
          <span className="step-badge">2</span>
          <h2 className="section-title">{t("Berekeningsmethode")}</h2>
          <HelpIcon open={helpOpen.s2} onClick={() => toggleHelp("s2")} label={t("Uitleg berekeningsmethode")} />
        </div>
        <div className={`help-panel ${helpOpen.s2 ? "help-panel-open" : ""}`}>
          <div className="help-panel-eyebrow">{t("Uitleg")}</div>
          <p>
            {t("We berekenen de jaarlijkse warmtevraag via gasverbruik of via EP1 (energieprestatie). Gasverbruik is meestal nauwkeuriger. EP1 is een goede fallback wanneer de jaarnota niet beschikbaar is.")}
          </p>
          <div className="help-panel-sub-title">{t("Totaal gasverbruik")}</div>
          <p>
            {t("Te vinden op de jaarnota. Gebruik het meest recente volledige jaar vóór installatie van een hybride. Bij een lange wintervakantie (>2 weken): gebruik het gemiddelde van de niet-vakantiemaanden × 12.")}
          </p>
          <div className="help-panel-sub-title">{t("Gemiddeld zomerverbruik")}</div>
          <p>
            {t("Juni, juli en augustus van de jaarnota. In die maanden gaat gas alleen naar warm water en koken. Dit cijfer × 12 wordt afgetrokken van het totaal om het verwarmingsdeel te isoleren.")}
          </p>
          <div className="help-panel-sub-title">EP1 / BENG1</div>
          <p>
            {t("Het veld")} <strong>{t("Warmtebehoefte")}</strong> {t("(kWh/m²/jaar) op")}{" "}
            <a href="https://www.ep-online.nl" target="_blank" rel="noopener noreferrer">ep-online.nl</a>{" "}
            {t('(zoek op postcode + huisnummer, klik op "Detailgegevens"). Vul ook het thermisch oppervlak in.')}
          </p>
        </div>

        <div className="question">
          <div className="radio-cards">
            <button
              type="button"
              className={`radio-card ${s.route === "gas" ? "selected" : ""}`}
              onClick={() => update("route", "gas")}
            >
              <svg className="radio-card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="radio-card-label">{t("Gasverbruik")}</div>
              <div className="radio-card-sub">{t("op basis van gasrekening")}</div>
            </button>
            <button
              type="button"
              className={`radio-card ${s.route === "beng1" ? "selected" : ""}`}
              onClick={() => update("route", "beng1")}
            >
              <svg className="radio-card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <div className="radio-card-label">BENG1 / EP1</div>
              <div className="radio-card-sub">{t("op basis van energieprestatie")}</div>
            </button>
          </div>
        </div>

        <div className="question">
          {s.route === "gas" ? (
            <div className="input-row">
              <div className="input-group">
                <label className="input-label">{t("Jaarlijks gasverbruik (m³)")}</label>
                <input
                  className="text-input"
                  type="number"
                  value={s.yearly_gas_m3}
                  onChange={(e) => update("yearly_gas_m3", e.target.value)}
                  placeholder="1500"
                />
                <div className="helper-text">{t("Gemiddelde over laatste 2-3 jaar")}</div>
              </div>
              <div className="input-group">
                <label className="input-label">{t("Zomergemiddelde (m³/maand)")}</label>
                <input
                  className="text-input"
                  type="number"
                  value={s.summer_avg_gas_m3}
                  onChange={(e) => update("summer_avg_gas_m3", e.target.value)}
                  placeholder="50"
                />
                <div className="helper-text">{t("Jun-Aug gemiddelde (warm water)")}</div>
              </div>
            </div>
          ) : (
            <div className="input-row">
              <div className="input-group">
                <label className="input-label">{t("BENG1 / EP1 waarde (kWh/m²)")}</label>
                <input
                  className="text-input"
                  type="number"
                  value={s.beng1_value}
                  onChange={(e) => update("beng1_value", e.target.value)}
                  placeholder="125"
                />
                <div className="helper-text">{t("Energieprestatie-indicator")}</div>
              </div>
              <div className="input-group">
                <label className="input-label">{t("Gebruiksoppervlakte (m²)")}</label>
                <input
                  className="text-input"
                  type="number"
                  value={s.floor_area_m2}
                  onChange={(e) => update("floor_area_m2", e.target.value)}
                  placeholder="135"
                />
                <div className="helper-text">{t("Bruto vloeroppervlak")}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Section 3. Isolatie & woning */}
      <div className="card">
        <div className="section-header">
          <span className="step-badge">3</span>
          <h2 className="section-title">{t("Isolatie & woning")}</h2>
          <HelpIcon open={helpOpen.s3} onClick={() => toggleHelp("s3")} label={t("Uitleg isolatie")} />
        </div>
        <div className={`help-panel ${helpOpen.s3 ? "help-panel-open" : ""}`}>
          <div className="help-panel-eyebrow">{t("Uitleg")}</div>
          <p>
            {t("Isolatiekwaliteit bepaalt de warmtebehoefte per m² en daarmee de capaciteit die een All-Electric warmtepomp moet leveren bij -7°C buiten. Een goed geïsoleerde woning heeft een lagere piekvraag en past sneller binnen Solo-capaciteit.")}
          </p>
          <div className="help-panel-sub-title">{t("Energielabel")}</div>
          <p>
            {t("Het energielabel staat op het energiecertificaat of is opzoekbaar via")}{" "}
            <a href="https://www.ep-online.nl" target="_blank" rel="noopener noreferrer">ep-online.nl</a>{" "}
            {t("(postcode + huisnummer).")} <strong>A / A+ / A++ / A+++</strong> = {t("Goed")},{" "}
            <strong>B / C</strong> = {t("Gemiddeld")}, <strong>{t("D of lager")}</strong> = {t("Slecht")}. {t("Bij twijfel: kies Gemiddeld.")}
          </p>
          <div className="help-panel-sub-title">{t("Isolatiemaatregelen")}</div>
          <p>
            {t("Vink aan welke maatregelen al uitgevoerd zijn. Bij D-G label maar wel HR++/triple glas + spouw + dak gecombineerd kan de woning alsnog geschikt zijn. Vooral als de CV-aanvoer comfortabel onder 60°C blijft.")}
          </p>
        </div>

        <div className="question">
          <label className="question-label">{t("Bouwjaar")}</label>
          <div className="pill-group">
            {BUILD_YEARS.map((b) => (
              <button
                key={b.v}
                type="button"
                className={`pill-btn ${s.build_year === b.v ? "active" : ""}`}
                onClick={() => update("build_year", b.v)}
              >
                {t(b.l)}
              </button>
            ))}
          </div>
        </div>

        <div className="question">
          <div className="input-group">
            <label className="input-label">{t("Energielabel (optioneel)")}</label>
            <select
              className="select-input"
              value={s.energy_label}
              onChange={(e) => update("energy_label", e.target.value)}
            >
              {ENERGY_LABELS.map((l) => (
                <option key={l} value={l}>{l || t("Niet bekend")}</option>
              ))}
            </select>
          </div>
          {labelIsBad && (
            <div className="info-note">
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t("Let op: bij dit label is All-Electric vaak ongeschikt.")}
            </div>
          )}
        </div>

        <div className="question">
          <label className="question-label">{t("Isolatiemaatregelen (meerdere mogelijk)")}</label>
          <div className="checkbox-grid">
            {INSULATION_OPTIONS.map((m) => {
              const checked = s.insulation_measures.includes(m.v);
              return (
                <div
                  key={m.v}
                  role="checkbox"
                  aria-checked={checked}
                  tabIndex={0}
                  className={`checkbox-card ${checked ? "checked" : ""}`}
                  onClick={() => toggleArrayValue("insulation_measures", m.v)}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      toggleArrayValue("insulation_measures", m.v);
                    }
                  }}
                >
                  <div className="custom-checkbox">
                    <CheckIcon />
                  </div>
                  <span className="checkbox-label">{t(m.l)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 4. Verwarmingssysteem & temperatuur */}
      <div className="card">
        <div className="section-header">
          <span className="step-badge">4</span>
          <h2 className="section-title">{t("Verwarmingssysteem & temperatuur")}</h2>
          <HelpIcon open={helpOpen.s4} onClick={() => toggleHelp("s4")} label={t("Uitleg verwarmingssysteem")} />
        </div>
        <div className={`help-panel ${helpOpen.s4 ? "help-panel-open" : ""}`}>
          <div className="help-panel-eyebrow">{t("Uitleg")}</div>
          <p>
            {t("Een warmtepomp levert water op een lagere temperatuur dan een CV-ketel. Deze stap bepaalt of het bestaande afgiftesysteem voldoende warmte kan leveren op die lagere temperatuur. En welke aanvoertemperatuur (W35 / W45 / W55) nodig is.")}
          </p>
          <div className="help-panel-sub-title">{t("CV-aanvoertemperatuur")}</div>
          <p>
            {t('Kijk op het display van de CV-ketel ("aanvoer-" of "stooktemperatuur"). Geen display? Check de ketel-app of het installatieblad in de meterkast. Typische waarden: 60-80°C voor oudere systemen, 50-60°C voor nieuwere.')}
          </p>
          <div className="help-panel-sub-title">{t("Hoe dit de warmtevraag beïnvloedt")}</div>
          <p>
            {t("Hoe lager de huidige CV-instelling, hoe lager de aanvoertemperatuur die de warmtepomp moet leveren. Op W35 levert de unit significant meer vermogen dan op W55. Een hoge CV-instelling met alleen radiatoren verhoogt de kans op een tekort op koude dagen.")}
          </p>
          <div className="help-panel-sub-title">{t("Verwarmingsdistributie")}</div>
          <p>
            {t("Vloerverwarming werkt op 35-45°C en is altijd geschikt. Aanvoer hangt af van de pompgroep:")} <strong>{t("zonder mengpompgroep → W35")}</strong>,{" "}
            <strong>{t("met mengpompgroep → W45")}</strong>. {t("Radiatoren zijn meestal ook geschikt, maar bij een hoge CV-instelling (>60°C) wordt een radiatorcheck aanbevolen.")}
          </p>
        </div>

        <div className="question">
          <label className="question-label">{t("CV comfortabel op 55°C?")}</label>
          <div className="pill-group">
            <button type="button" className={`pill-btn ${s.cv_comfort_55c ? "active" : ""}`} onClick={() => update("cv_comfort_55c", true)}>{t("Ja")}</button>
            <button type="button" className={`pill-btn ${!s.cv_comfort_55c ? "active" : ""}`} onClick={() => update("cv_comfort_55c", false)}>{t("Nee")}</button>
          </div>
          <div className="helper-text">{t("Belangrijk voor All-Electric. Bij Nee wordt de woning gemarkeerd als extra controle.")}</div>
        </div>

        <div className="question">
          <label className="question-label">{t("Stille modus 's nachts?")}</label>
          <div className="pill-group">
            <button type="button" className={`pill-btn ${s.silent_mode_at_night ? "active" : ""}`} onClick={() => update("silent_mode_at_night", true)}>{t("Ja")}</button>
            <button type="button" className={`pill-btn ${!s.silent_mode_at_night ? "active" : ""}`} onClick={() => update("silent_mode_at_night", false)}>{t("Nee")}</button>
          </div>
          <div className="helper-text">{t("Bij Ja rekenen we met lager vermogen 's nachts (stille modus, minder geluid voor de buren). Bij Nee gebruiken we het volle vermogen 24/7 - meer reserve, maar mogelijk meer geluid op koude nachten.")}</div>
        </div>

        <div className="question">
          <div className="input-group">
            <label className="input-label">{t("Verwarmingssysteem hele woning")}</label>
            <select
              className="select-input"
              value={s.distribution_whole_house}
              onChange={(e) => update("distribution_whole_house", e.target.value)}
            >
              {DISTRIBUTION_OPTIONS.map((d) => (
                <option key={d.v} value={d.v}>{t(d.l)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="question">
          <div className="input-group">
            <label className="input-label">{t("Hoofdruimte distributie")}</label>
            <select
              className="select-input"
              value={s.distribution_main_room}
              onChange={(e) => update("distribution_main_room", e.target.value)}
            >
              {DISTRIBUTION_OPTIONS.map((d) => (
                <option key={d.v} value={d.v}>{t(d.l)}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="question">
          <div className="input-row">
            <div className="input-group">
              <label className="input-label">{t("Dagtemperatuur (°C)")}</label>
              <input
                className="text-input"
                type="number"
                value={s.day_temp}
                onChange={(e) => update("day_temp", e.target.value)}
                placeholder="20"
              />
            </div>
            <div className="input-group">
              <label className="input-label">{t("Nachttemperatuur (°C)")}</label>
              <input
                className="text-input"
                type="number"
                value={s.night_temp}
                onChange={(e) => update("night_temp", e.target.value)}
                placeholder="19"
              />
            </div>
          </div>
          <div className={`temp-delta-pill ${tempWarn ? "warning" : ""}`}>
            {t("Verschil:")} <strong>&nbsp;{Number.isFinite(tempDelta) ? tempDelta : 0} °C</strong>
            {tempWarn && <>&nbsp;- {t("Grote setback, ongunstig voor warmtepomp.")}</>}
          </div>
        </div>

        {/* CV supply temperature. primary input in Section 4 (HTML Step 2 parity). */}
        <div className="question">
          <div className="input-group">
            <label className="input-label">{t("Huidige CV-aanvoertemperatuur (°C)")}</label>
            <input
              className="text-input"
              type="number"
              min="20"
              max="90"
              value={s.cv_temp_c}
              onChange={(e) => update("cv_temp_c", e.target.value)}
              placeholder="65"
            />
            <div className="helper-text">
              {t("Stookt de woning typisch op 60 °C of hoger? Vul de waarde in voor een exactere controle. Bij lege invoer rekenen we met de keuze hierboven.")}
              {isRadiatorSystem && (
                <>
                  <br />
                  <strong>{t("Tip:")}</strong> {t("bij een CV-aanvoer > 60 °C op radiatoren verschijnt de Radiatorcheck (stap 5).")}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Manifold pump. only relevant for vloerverwarming (HTML parity). */}
        {(s.distribution_whole_house === "Vloerverwarming" ||
          s.distribution_whole_house === "Vloerverwarming + andere bronnen") && (
          <div className="question">
            <div className="question-label-row">
              <label className="question-label">{t("Verdeler met pomp aanwezig?")}</label>
              <HelpIcon
                open={helpOpen.q_manifold_pump}
                onClick={() => toggleHelp("q_manifold_pump")}
                label={t("Uitleg verdeler met pomp")}
              />
            </div>
            <div className={`help-panel ${helpOpen.q_manifold_pump ? "help-panel-open" : ""}`}>
              <p>
                <strong>{t("Met pomp (gesloten / mengverdeler):")}</strong> {t("mengt heet ketelwater met retourwater tot vloertemperatuur. Gebruikt bij traditionele CV-ketels of systemen met meer dan 6-7 groepen. Hogere aanvoertemperatuur vereist (W45).")}
              </p>
              <p>
                <strong>{t("Zonder pomp (open / directe verdeler):")}</strong> {t("gebruikt de pomp van de warmtepomp zelf, geen actieve menging. Energiezuiniger, stiller, minder onderhoud. Standaardkeuze bij warmtepompen en goed geïsoleerde woningen (W35).")}
              </p>
            </div>
            <div className="pill-group">
              <button
                type="button"
                className={`pill-btn ${s.manifold_pump === true ? "active" : ""}`}
                onClick={() => update("manifold_pump", true)}
              >
                {t("Ja")}
              </button>
              <button
                type="button"
                className={`pill-btn ${s.manifold_pump === false ? "active" : ""}`}
                onClick={() => update("manifold_pump", false)}
              >
                {t("Nee")}
              </button>
            </div>
            <div className="helper-text">
              {t("Bij vloerverwarming met verdeler-pomp rekent de check met W45, zonder pomp met W35.")}
            </div>
          </div>
        )}

        {/* Zone-thermostaat. Amber (extra_controle), niet blokkerend. */}
        <div className="question">
          <div className="question-label-row">
            <label className="question-label">{t("Zone-thermostaat aanwezig?")}</label>
            <HelpIcon
              open={helpOpen.q_zone_thermostat}
              onClick={() => toggleHelp("q_zone_thermostat")}
              label={t("Uitleg zone-thermostaat")}
            />
          </div>
          <div className={`help-panel ${helpOpen.q_zone_thermostat ? "help-panel-open" : ""}`}>
            <p>
              {t("Zone-thermostaten regelen de temperatuur per kamer of per zone (Tado, Honeywell Evohome, Anna). De warmtepomp-regeling zelf is voor hybride en All-Electric gelijk -- het is geen technische blokker.")}
            </p>
            <p>
              {t('Wel relevant voor All-E: er is minder backup-vermogen dan bij hybride, de marges zijn dunner, zone-regeling tilt in de praktijk de watertemperatuur omhoog (lagere COP) en maakt het systeem complexer en storingsgevoeliger. Bij Ja komt de woning op "extra controle" te staan, niet automatisch ongeschikt -- laat de installateur meekijken.')}
            </p>
          </div>
          <div className="pill-group">
            <button
              type="button"
              className={`pill-btn ${s.zone_thermostat === "ja" ? "active" : ""}`}
              onClick={() => update("zone_thermostat", "ja")}
            >
              {t("Ja")}
            </button>
            <button
              type="button"
              className={`pill-btn ${s.zone_thermostat === "nee" ? "active" : ""}`}
              onClick={() => update("zone_thermostat", "nee")}
            >
              {t("Nee")}
            </button>
          </div>
          <div className="helper-text">
            {t("Niet blokkerend -- regelt voor hybride en All-E hetzelfde. Wel: minder backup-vermogen, hogere watertemperaturen (lagere COP), complexer systeem.")}
          </div>
        </div>

        {/* Extra warmtebronnen. HTML amber flag when "ja". */}
        <div className="question">
          <div className="question-label-row">
            <label className="question-label">{t("Extra warmtebronnen (open haard, airco, IR-paneel)?")}</label>
            <HelpIcon
              open={helpOpen.q_extra_sources}
              onClick={() => toggleHelp("q_extra_sources")}
              label={t("Uitleg extra warmtebronnen")}
            />
          </div>
          <div className={`help-panel ${helpOpen.q_extra_sources ? "help-panel-open" : ""}`}>
            <p>
              {t("Extra warmtebronnen (open haard, airco met heat-modus, infraroodpaneel) leveren ondersteunende warmte op de koudste dagen. Daardoor mag de capaciteitsdekking soms iets lager liggen. De bewoner accepteert dat de warmtepomp niet alle vraag dekt en vult bij met de extra bron.")}
            </p>
            <p>
              {t("Bij")} <strong>{t("Ja")}</strong> {t("verlagen we de dekkingsdrempel licht. Bij")} <strong>{t("Nee")}</strong>{" "}
              {t("moet de warmtepomp de volledige piek bij -7°C zelf dragen.")}
            </p>
          </div>
          <div className="pill-group">
            <button
              type="button"
              className={`pill-btn ${s.extra_heat_sources === "ja" ? "active" : ""}`}
              onClick={() => update("extra_heat_sources", "ja")}
            >
              {t("Ja")}
            </button>
            <button
              type="button"
              className={`pill-btn ${s.extra_heat_sources === "nee" ? "active" : ""}`}
              onClick={() => update("extra_heat_sources", "nee")}
            >
              {t("Nee")}
            </button>
          </div>
          <div className="helper-text">
            {t("Extra warmtebronnen kunnen de sizing beïnvloeden. Soms is een lagere dekking acceptabel.")}
          </div>
        </div>

        {/* CW-klasse. HTML amber flag when "CW6". */}
        <div className="question">
          <div className="input-group">
            <div className="input-label-row">
              <label className="input-label">{t("Tapwaterklasse (CW)")}</label>
              <HelpIcon
                open={helpOpen.q_cw_class}
                onClick={() => toggleHelp("q_cw_class")}
                label={t("Uitleg CW-klasse")}
              />
            </div>
            <div className={`help-panel ${helpOpen.q_cw_class ? "help-panel-open" : ""}`}>
              <p>
                {t('CW staat voor "comfortklasse warm water". Het geeft aan hoe snel de CV-ketel warm tapwater levert.')} <strong>CW5</strong> {t("is standaard voor de meeste woningen (één of twee badkamers, normaal gebruik).")} <strong>CW6</strong> {t("is voor grote woningen met meerdere badkamers tegelijk in gebruik.")}
              </p>
              <p>
                {t("De Quatt All-Electric warmtepomp is functioneel gelijkwaardig aan CW5. Heeft de klant bewust een CW6-ketel? Vraag dan of de reden daarvoor bij All-Electric ook geldt, of dat CW5-capaciteit in de praktijk volstaat.")}
              </p>
              <p>
                <strong>{t("Waar te vinden:")}</strong> {t("op het typeplaatje van de CV-ketel, in de handleiding, of via de Mijnketel-portal. Geen klasse vermeld? Dan is het vrijwel altijd CW5.")}
              </p>
            </div>
            <select
              className="select-input"
              value={s.cw_class}
              onChange={(e) => update("cw_class", e.target.value as RecommenderFormState["cw_class"])}
            >
              <option value="">{t("Onbekend")}</option>
              <option value="CW5">CW5</option>
              <option value="CW6">CW6</option>
            </select>
            <div className="helper-text">
              {t("Quatt All-Electric is vergelijkbaar met CW5. Bij CW6 controleren we of die piekvraag echt nodig is.")}
            </div>
          </div>
        </div>
      </div>

      {/* Section 5. Radiatoren (conditional: hot CV + radiator system) */}
      {showRadiatorStep && (
        <div className="card">
          <div className="section-header">
            <span className="step-badge">5</span>
            <h2 className="section-title">{t("Radiatoren")}</h2>
            <HelpIcon open={helpOpen.s5} onClick={() => toggleHelp("s5")} label={t("Uitleg radiatorcheck")} />
          </div>
          <div className={`help-panel ${helpOpen.s5 ? "help-panel-open" : ""}`}>
            <div className="help-panel-eyebrow">{t("Uitleg")}</div>
            <p>
              {t("Radiatoren zijn genormeerd (EN 442) op 75°C aanvoer / 65°C retour / 20°C ruimte. Op 55°C aanvoer levert een radiator nog maar ~52% van zijn nominale vermogen. Deze check bepaalt of het totale radiatoroppervlak voldoende is bij de lagere aanvoertemperatuur van de warmtepomp.")}
            </p>
            <div className="help-panel-sub-title">{t("Radiatortypen bij W55 (W/m²)")}</div>
            <p>
              <strong>10</strong>. {t("enkel vlak paneel, geen lamellen: 270")} &middot;{" "}
              <strong>11</strong>. {t("enkel met lamellen: 460")} &middot;{" "}
              <strong>20</strong>. {t("dubbel vlak: 536")} &middot;{" "}
              <strong>21</strong>. {t("dubbel + 1 lamel: 663")} &middot;{" "}
              <strong>22</strong>. {t("dubbel + 2 lamellen: 918")} &middot;{" "}
              <strong>33</strong>. {t("driedubbel + 3 lamellen: 1326.")}
            </p>
            <p>
              {t("Het type staat op een sticker op de zijkant of achterkant van de radiator. herkenbaar aan het aantal panelen en lamelrijen.")}
            </p>
            <div className="help-panel-sub-title">{t("Dekkingspercentage")}</div>
            <p>
              {t("Vul alleen radiatoren in die normaal openstaan: woonkamer, keuken, slaapkamers. Sla kleine of zelden gebruikte ruimtes over. Het percentage geeft aan welk aandeel van de totale warmtevraag de ingevulde radiatoren moeten dragen.")}
            </p>
          </div>

          <p className="card-subtitle">
            {t("Bij een hoge CV-aanvoer controleren we of de bestaande radiatoren ook voldoende vermogen leveren bij W55 (de typische aanvoer van een warmtepomp). Vul de grootste radiatoren in. Klein of zelden gebruikte ruimtes mag je overslaan.")}
            {s.cv_temp_c && (
              <>
                {" "}{t("Huidige CV-aanvoer:")} <strong>{s.cv_temp_c}&nbsp;&deg;C</strong> {t("(zie stap 4).")}
              </>
            )}
          </p>

          <div className="question">
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
              }}
            >
              <thead>
                <tr style={{ textAlign: "left", color: "var(--muted)", fontSize: 12 }}>
                  <th style={{ padding: "6px 8px 6px 0", fontWeight: 500, width: 28 }}>#</th>
                  <th style={{ padding: "6px 8px 6px 0", fontWeight: 500 }}>{t("Ruimte")}</th>
                  <th style={{ padding: "6px 8px 6px 0", fontWeight: 500 }}>Type</th>
                  <th style={{ padding: "6px 8px 6px 0", fontWeight: 500 }}>{t("Lengte (cm)")}</th>
                  <th style={{ padding: "6px 8px 6px 0", fontWeight: 500 }}>{t("Hoogte (cm)")}</th>
                  <th style={{ padding: "6px 0", fontWeight: 500, textAlign: "right" }}>
                    {t("Vermogen (kW)")}
                  </th>
                  <th style={{ padding: "6px 0 6px 8px", width: 36 }} aria-label={t("Actie")} />
                </tr>
              </thead>
              <tbody>
                {s.radiators.map((r, idx) => (
                  <tr
                    key={idx}
                    style={{ borderTop: "1px solid rgba(19,26,32,0.08)" }}
                  >
                    <td style={{ padding: "8px 8px 8px 0", color: "var(--muted)" }}>
                      {idx + 1}
                    </td>
                    <td style={{ padding: "8px 8px 8px 0" }}>
                      <input
                        className="text-input"
                        type="text"
                        value={r.room}
                        onChange={(e) => updateRadiator(idx, { room: e.target.value })}
                        placeholder={t("Woonkamer")}
                      />
                    </td>
                    <td style={{ padding: "8px 8px 8px 0" }}>
                      <select
                        className="select-input"
                        value={r.type}
                        onChange={(e) =>
                          updateRadiator(idx, {
                            type: e.target.value as RecommenderFormState["radiators"][number]["type"],
                          })
                        }
                      >
                        <option value="">-</option>
                        {RADIATOR_TYPES.map((rt) => (
                          <option key={rt.v} value={rt.v}>
                            {rt.l}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: "8px 8px 8px 0" }}>
                      <input
                        className="text-input"
                        type="number"
                        value={r.length_cm}
                        onChange={(e) => updateRadiator(idx, { length_cm: e.target.value })}
                        placeholder="120"
                      />
                    </td>
                    <td style={{ padding: "8px 8px 8px 0" }}>
                      <input
                        className="text-input"
                        type="number"
                        value={r.height_cm}
                        onChange={(e) => updateRadiator(idx, { height_cm: e.target.value })}
                        placeholder="60"
                      />
                    </td>
                    <td
                      style={{
                        padding: "8px 0",
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        color: radiatorTotals.perRow[idx]?.kw
                          ? "var(--ink)"
                          : "var(--muted)",
                      }}
                    >
                      {radiatorTotals.perRow[idx]?.kw
                        ? `${radiatorTotals.perRow[idx].kw.toFixed(2)} kW`
                        : "-"}
                    </td>
                    <td style={{ padding: "8px 0 8px 8px", textAlign: "right" }}>
                      {s.radiators.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRadiator(idx)}
                          aria-label={`${t("Radiator")} ${idx + 1} ${t("verwijderen")}`}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--muted)",
                            padding: 4,
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={addRadiator}
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v14M5 12h14" />
                </svg>
                {t("Radiator toevoegen")}
              </button>
            </div>
          </div>

          <div className="question">
            <div className="input-group">
              <label className="input-label">{t("Ingevuld aandeel van totale warmtevraag (%)")}</label>
              <input
                className="text-input"
                type="number"
                min="1"
                max="100"
                value={s.radiator_coverage_pct}
                onChange={(e) => update("radiator_coverage_pct", e.target.value)}
                placeholder="70"
              />
              <div className="helper-text">
                {t("Welk deel van de woning heb je ingevuld? Standaard 70%.")}
              </div>
            </div>
          </div>

          <div
            className="info-note"
            style={{ marginTop: 4 }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              {t("Totaal vermogen:")} <strong>{radiatorTotals.totalKw > 0 ? `${radiatorTotals.totalKw.toFixed(2)} kW` : "-"}</strong>
              {` @ W55 \u00B7 ${t("Benodigd:")} `}
              <strong>{radiatorTotals.needed && radiatorTotals.needed > 0 ? `${radiatorTotals.needed.toFixed(2)} kW` : "-"}</strong>
              {` \u00B7 ${t("Dekking:")} `}
              <strong>{radiatorTotals.capPct !== null ? `${radiatorTotals.capPct}%` : "-"}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Optional accordion. Voor preciezere aanbeveling */}
      <div className="card warm">
        <button
          type="button"
          className={`accordion-trigger ${showAdvanced ? "is-open" : ""}`}
          onClick={() => setShowAdvanced((v) => !v)}
          aria-expanded={showAdvanced}
        >
          <div className="section-header no-margin">
            <span className="step-badge">+</span>
            <h2 className="section-title">{t("Voor preciezere aanbeveling")}</h2>
          </div>
          <ChevronIcon open={showAdvanced} />
        </button>
        <div className={`accordion-content ${showAdvanced ? "open" : ""}`}>
          <p className="card-subtitle">
            {t("Bewoners, ketelleeftijd, warmwatergebruik en isolatiescores per element. Alleen invullen als bekend. Geeft een nauwkeurigere capaciteitsberekening (BENG2 + COP-cluster) en daarmee een scherpere Solo / Duo aanbeveling.")}
          </p>

          <div className="question">
            <div className="input-row">
              <div className="input-group">
                <label className="input-label">{t("Aantal bewoners")}</label>
                <input
                  className="text-input"
                  type="number"
                  value={s.resident_number}
                  onChange={(e) => update("resident_number", e.target.value)}
                  placeholder="3"
                />
              </div>
              <div className="input-group">
                <label className="input-label">{t("Ketelleeftijd")}</label>
                <div className="pill-group" style={{ marginTop: 6 }}>
                  {BOILER_AGE_OPTIONS.map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      className={`pill-btn ${s.boiler_age === o.v ? "active" : ""}`}
                      onClick={() => update("boiler_age", o.v)}
                    >
                      {t(o.l)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="question">
            <div className="input-row">
              <div className="input-group">
                <label className="input-label">{t("Warmwatergebruik")}</label>
                <div className="pill-group" style={{ marginTop: 6 }}>
                  {HOT_WATER_OPTIONS.map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      className={`pill-btn ${s.hot_water_usage === o.v ? "active" : ""}`}
                      onClick={() => update("hot_water_usage", o.v)}
                    >
                      {t(o.l)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="input-group">
                <label className="input-label">{t("Geschat elektriciteitsverbruik (kWh/jaar)")}</label>
                <input
                  className="text-input"
                  type="number"
                  value={s.estimated_electricity_usage}
                  onChange={(e) => update("estimated_electricity_usage", e.target.value)}
                  placeholder="3200"
                />
              </div>
            </div>
          </div>

          <div className="question">
            <label className="question-label">{t("Isolatiescores per element (0-10, optioneel)")}</label>
            <div className="helper-text" style={{ marginBottom: 12 }}>{t("10 = perfect, 5 = gemiddeld, 0 = geen isolatie.")}</div>
            <div className="slider-group">
              {insulationSliders.map(({ name, key }) => {
                const raw = s[key];
                const display = raw === "" ? "-" : raw;
                const sliderValue = raw === "" ? 5 : Number(raw);
                return (
                  <div key={key} className="slider-item">
                    <label className="slider-label">{name}</label>
                    <input
                      type="range"
                      className="slider-input"
                      min={0}
                      max={10}
                      value={sliderValue}
                      onChange={(e) => update(key, e.target.value)}
                    />
                    <div className="slider-value">{display}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Submit row */}
      <div className="submit-row">
        <button type="submit" className="btn-primary" disabled={isSubmitting}>
          {isSubmitting ? t("Bezig...") : t("Bereken aanbeveling")}
        </button>
        <button type="button" className="btn-secondary" onClick={() => setS(defaultState())}>
          Reset
        </button>
      </div>
    </form>
  );
}
